import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { SectionCompleteToggle } from '../../components/SectionCompleteToggle';
import { Delete, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable, MRT_Row, MRT_Cell } from 'material-react-table';
import { CoverPageDTO, CoverPageBudgetRowDTO, CoverPageGraphType } from '../../../magic-report/magic-report.type';
import { formatCurrency } from 'common/utils/common.util';
import { IconOverTextButton } from '../../../../common/IconOverTextButton';
import WhiteFab from '../../../../common/WhiteFab';
import { BudgetSummaryDTO, DrawSummary } from '../../../../project/project.type';
import { roundCompletionPercent } from '../../utils/percent.util';

interface CoverPageTableProps {
  coverPage: CoverPageDTO | null | undefined;
  budgetSummary?: Partial<BudgetSummaryDTO>;
  drawName?: string;
  /**
   * Every draw the report covers. One budget row is seeded per draw, carrying that draw's
   * contractor / contract name / application number so the cover letter and §1.1 can print
   * "$X for {contractor}'s {contract} Application #{n}" on each line. Rows the user adds by hand
   * (e.g. "Soft Costs") sit after these and are untouched.
   */
  draws?: Array<{ drawId: string; drawSummary?: Partial<DrawSummary>; budgetSummary?: Partial<BudgetSummaryDTO> }>;
  /** Contractor name for draw-seeded rows. */
  contractor?: string;
  onChange: (coverPage: CoverPageDTO) => void;
  isCompleted?: boolean;
  onToggleComplete?: () => void;
  isSavingComplete?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

type NewBudgetRowFormState = {
  budgetName: string;
  requestedAmount: string;
  amountRecommendedForFunding: string;
  percentComplete: string;
};

const createEmptyBudgetRowFormState = (): NewBudgetRowFormState => ({
  budgetName: '',
  requestedAmount: '',
  amountRecommendedForFunding: '',
  percentComplete: '',
});

/**
 * The budget rows a report's covered draws imply — one per draw, in draw order, each tagged so the
 * printed amount lines can name their own contract and application.
 */
const buildDrawBackedRows = (draws: CoverPageTableProps['draws'], contractor: string | undefined): CoverPageBudgetRowDTO[] =>
  (draws || []).map(draw => ({
    drawId: draw.drawId,
    budgetName: draw.drawSummary?.name || 'Main Budget',
    contractor,
    contractName: draw.drawSummary?.name,
    applicationNumber: draw.drawSummary?.applicationNumber,
    requestedAmount: draw.budgetSummary?.requestedAmount || 0,
    amountRecommendedForFunding: draw.budgetSummary?.totalAmountReccForFunding ?? 0,
    percentComplete: draw.budgetSummary?.totalContractBudgetPerComplete || 0,
  }));

/**
 * Merges draw-backed rows into whatever is stored: each draw's row keeps its live amounts from that
 * draw's budget, hand-added rows are preserved in order, and rows for draws no longer covered are
 * dropped. Returns the same array reference when nothing changed so callers can skip a save.
 *
 * %Complete follows the draw too, unless the reviewer typed one (`isPercentCompleteOverridden`).
 * Rows saved before that flag existed carry an auto-seeded value that may be stale, so they are
 * refreshed like any other un-typed row.
 *
 * `overwriteEdits` is for the Refresh button: the reviewer is asking for the budget's current
 * numbers, so the derived %Complete is taken from the draw instead of keeping their typed value.
 */
const reconcileDrawBackedRows = (
  storedRows: CoverPageBudgetRowDTO[],
  drawRows: CoverPageBudgetRowDTO[],
  { overwriteEdits = false }: { overwriteEdits?: boolean } = {},
): CoverPageBudgetRowDTO[] => {
  if (drawRows.length === 0) return storedRows;

  const storedByDrawId = new Map(storedRows.filter(r => r.drawId).map(r => [r.drawId as string, r]));
  const untaggedRows = storedRows.filter(r => !r.drawId);

  // Rows written before draw-tagging — and any row that came back from a save untagged — stand in
  // for the covered draws in stored order. Adopt the first one per draw instead of seeding a fresh
  // row beside them, or every reconcile would leave the table one extra copy of each draw longer.
  const hasTaggedRows = storedByDrawId.size > 0;
  const adoptedRows = hasTaggedRows ? [] : untaggedRows.slice(0, drawRows.length);
  const manualRows = hasTaggedRows ? untaggedRows : untaggedRows.slice(drawRows.length);

  const nextDrawRows = drawRows.map((drawRow, index) => {
    const existing = storedByDrawId.get(drawRow.drawId as string) ?? adoptedRows[index];
    const keepTypedPercent = !overwriteEdits && !!existing?.isPercentCompleteOverridden;
    return {
      ...drawRow,
      // Keep the reviewer's own edits to the fields we don't derive — unless they asked for a
      // refresh, which restores the draw's own name and %Complete.
      budgetName: (overwriteEdits ? drawRow.budgetName : existing?.budgetName) || drawRow.budgetName,
      percentComplete: keepTypedPercent ? (existing?.percentComplete ?? null) : drawRow.percentComplete,
      isPercentCompleteOverridden: keepTypedPercent,
    };
  });

  const next = [...nextDrawRows, ...manualRows];

  // Stored rows come back from the API with null where live rows can hold undefined, and amounts
  // round-trip through a decimal. Exact comparison would see a change on every load and mark the
  // report dirty before the reviewer touches anything.
  const sameValue = (a: unknown, b: unknown) => (a ?? null) === (b ?? null);
  const sameAmount = (a: number | null | undefined, b: number | null | undefined) =>
    a == null || b == null ? sameValue(a, b) : Math.abs(a - b) < 0.005;

  const unchanged =
    next.length === storedRows.length &&
    next.every((row, i) => {
      const prev = storedRows[i];
      return (
        prev &&
        sameValue(prev.drawId, row.drawId) &&
        sameValue(prev.budgetName, row.budgetName) &&
        sameAmount(prev.requestedAmount, row.requestedAmount) &&
        sameAmount(prev.amountRecommendedForFunding, row.amountRecommendedForFunding) &&
        sameAmount(prev.percentComplete, row.percentComplete) &&
        !!prev.isPercentCompleteOverridden === !!row.isPercentCompleteOverridden &&
        sameValue(prev.contractor, row.contractor) &&
        sameValue(prev.contractName, row.contractName) &&
        sameValue(prev.applicationNumber, row.applicationNumber)
      );
    });

  return unchanged ? storedRows : next;
};

const CoverPageTable: React.FC<CoverPageTableProps> = ({
  coverPage,
  budgetSummary,
  drawName,
  draws,
  contractor,
  onChange,
  isCompleted = false,
  onToggleComplete,
  isSavingComplete = false,
  expanded = true,
  onExpandedChange,
}) => {
  const [localCoverPage, setLocalCoverPage] = useState<CoverPageDTO>(() => {
    if (coverPage) {
      // Ensure default values if coverPage has null values
      return {
        ...coverPage,
        budgetRows: coverPage.budgetRows || [],
        contingencyPercentageUsed: coverPage.contingencyPercentageUsed ?? null,
        isBalanceSufficient: coverPage.isBalanceSufficient ?? false,
      };
    }
    // Initialize from budget summary if available
    const initialRows: CoverPageBudgetRowDTO[] = [];
    if (budgetSummary?.requestedAmount !== undefined || budgetSummary?.totalAmountReccForFunding !== undefined) {
      initialRows.push({
        budgetName: drawName || 'Main Budget',
        requestedAmount: budgetSummary.requestedAmount || 0,
        amountRecommendedForFunding: budgetSummary.totalAmountReccForFunding ?? 0,
        percentComplete: budgetSummary.totalContractBudgetPerComplete || 0,
      });
    }
    return {
      budgetRows: initialRows,
      contingencyPercentageUsed: null,
      isBalanceSufficient: false,
      graphType: 'standard' as CoverPageGraphType,
      coverPagePhotoIds: [],
    };
  });

  const theme = useTheme();
  const localCoverPageRef = React.useRef(localCoverPage);
  localCoverPageRef.current = localCoverPage;
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newBudgetRowForm, setNewBudgetRowForm] = useState<NewBudgetRowFormState>(createEmptyBudgetRowFormState);
  const isContingencyNA = localCoverPage.contingencyPercentageUsed === null;
  const [contingencyInputValue, setContingencyInputValue] = useState<string>(
    localCoverPage.contingencyPercentageUsed == null ? '' : String(localCoverPage.contingencyPercentageUsed),
  );

  React.useEffect(() => {
    if (localCoverPage.contingencyPercentageUsed == null) {
      setContingencyInputValue('');
    } else {
      setContingencyInputValue(String(localCoverPage.contingencyPercentageUsed));
    }
  }, [localCoverPage.contingencyPercentageUsed]);

  // Every field is optional — a row can be added blank and filled in inline. Only values that are
  // present but invalid (non-numeric, negative, out-of-range percent) block the Add button.
  const isAddRowDisabled = useMemo(() => {
    if (newBudgetRowForm.requestedAmount.trim()) {
      const requestedAmountValue = Number(newBudgetRowForm.requestedAmount);
      if (!Number.isFinite(requestedAmountValue) || requestedAmountValue < 0) return true;
    }

    if (newBudgetRowForm.amountRecommendedForFunding.trim()) {
      const amountRecommendedValue = Number(newBudgetRowForm.amountRecommendedForFunding);
      if (!Number.isFinite(amountRecommendedValue) || amountRecommendedValue < 0) return true;
    }

    if (newBudgetRowForm.percentComplete.trim()) {
      const percentCompleteValue = Number(newBudgetRowForm.percentComplete);
      if (!Number.isFinite(percentCompleteValue) || percentCompleteValue < 0 || percentCompleteValue > 100) return true;
    }

    return false;
  }, [newBudgetRowForm]);

  const openAddRowDialog = () => {
    setNewBudgetRowForm(createEmptyBudgetRowFormState());
    setIsAddDialogOpen(true);
  };

  const handleCloseAddRowDialog = () => {
    setIsAddDialogOpen(false);
    setNewBudgetRowForm(createEmptyBudgetRowFormState());
  };

  const handleConfirmAddRow = () => {
    if (isAddRowDisabled) return;

    const newRow: CoverPageBudgetRowDTO = {
      budgetName: newBudgetRowForm.budgetName.trim(),
      requestedAmount: newBudgetRowForm.requestedAmount.trim() ? Number(newBudgetRowForm.requestedAmount) : 0,
      amountRecommendedForFunding: newBudgetRowForm.amountRecommendedForFunding.trim()
        ? Number(newBudgetRowForm.amountRecommendedForFunding)
        : 0,
      percentComplete: newBudgetRowForm.percentComplete.trim() ? Number(newBudgetRowForm.percentComplete) : null,
    };

    const budgetRows = localCoverPage.budgetRows || [];
    const updated = {
      ...localCoverPage,
      budgetRows: [...budgetRows, newRow],
    };
    setLocalCoverPage(updated);
    onChange(updated);

    setIsAddDialogOpen(false);
    setNewBudgetRowForm(createEmptyBudgetRowFormState());
  };

  const handleRowChange = React.useCallback(
    (rowIndex: number, field: keyof CoverPageBudgetRowDTO, value: string | number | null) => {
      setLocalCoverPage(prev => {
        const budgetRows = prev.budgetRows || [];
        // The cell commits on blur, so tabbing through it without a change must not mark the
        // %Complete as typed.
        if (field === 'percentComplete' && (budgetRows[rowIndex]?.percentComplete ?? null) === value) return prev;
        const updatedRows = [...budgetRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          [field]: value,
          // A typed %Complete (even a cleared one) stops following the budget until Refresh.
          ...(field === 'percentComplete' && { isPercentCompleteOverridden: true }),
        };
        const updated = { ...prev, budgetRows: updatedRows };
        onChange(updated);
        return updated;
      });
    },
    [onChange],
  );

  const handleDeleteRow = (row: MRT_Row<CoverPageBudgetRowDTO>) => {
    const budgetRows = localCoverPage.budgetRows || [];
    const updatedRows = budgetRows.filter((_, i) => i !== row.index);
    const updated = { ...localCoverPage, budgetRows: updatedRows };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  const handleContingencyChange = (value: string) => {
    const numValue = parseFloat(value);
    const updated = { ...localCoverPage, contingencyPercentageUsed: value === '' || isNaN(numValue) ? null : numValue };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  const handleContingencyNAChange = (checked: boolean) => {
    const updated = { ...localCoverPage, contingencyPercentageUsed: checked ? null : 0 };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  const handleBalanceSufficientChange = (checked: boolean) => {
    const updated = { ...localCoverPage, isBalanceSufficient: checked };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  const handleGraphTypeChange = (graphType: CoverPageGraphType) => {
    const updated = { ...localCoverPage, graphType };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  /**
   * Re-pulls the latest G702/3 numbers into the rows that already exist: one row per covered draw,
   * from that draw's own budget, with hand-added rows left alone. It never appends — an unchanged
   * budget leaves the table exactly as it was.
   */
  const handleRefreshFromBudget = () => {
    const storedRows = localCoverPage.budgetRows || [];
    const drawRows = buildDrawBackedRows(draws, contractor);

    let updatedRows: CoverPageBudgetRowDTO[];
    if (drawRows.length > 0) {
      updatedRows = reconcileDrawBackedRows(storedRows, drawRows, { overwriteEdits: true });
      if (updatedRows === storedRows) return;
    } else {
      if (!budgetSummary) return;
      const refreshedRow: CoverPageBudgetRowDTO = {
        ...storedRows[0],
        budgetName: storedRows[0]?.budgetName || drawName || 'Main Budget',
        requestedAmount: budgetSummary.requestedAmount || 0,
        amountRecommendedForFunding: budgetSummary.totalAmountReccForFunding ?? 0,
        percentComplete: budgetSummary.totalContractBudgetPerComplete || 0,
        isPercentCompleteOverridden: false,
      };
      updatedRows = [...storedRows];
      updatedRows[0] = refreshedRow;
    }

    const updated = { ...localCoverPage, budgetRows: updatedRows };
    setLocalCoverPage(updated);
    onChange(updated);
  };

  // Sync with external changes
  React.useEffect(() => {
    const local = localCoverPageRef.current;
    const currentBudgetRows = local.budgetRows || [];
    const hasEmptyRows = currentBudgetRows.length === 0;
    const drawRows = buildDrawBackedRows(draws, contractor);

    if (coverPage) {
      const coverPageRows = coverPage.budgetRows || [];

      // Keep one row per covered draw, with that draw's live amounts, ahead of any manual rows.
      if (drawRows.length > 0) {
        const reconciled = reconcileDrawBackedRows(coverPageRows, drawRows);
        const updated = { ...local, budgetRows: reconciled };
        setLocalCoverPage(updated);
        if (reconciled !== coverPageRows) onChange(updated);
        return;
      }

      if (
        coverPageRows.length === 0 &&
        budgetSummary &&
        (budgetSummary.requestedAmount !== undefined || budgetSummary.totalAmountReccForFunding !== undefined)
      ) {
        // Initialize from budget summary if coverPage has no rows
        const initialRows: CoverPageBudgetRowDTO[] = [
          {
            budgetName: drawName || 'Main Budget',
            requestedAmount: budgetSummary.requestedAmount || 0,
            amountRecommendedForFunding: budgetSummary.totalAmountReccForFunding ?? 0,
            percentComplete: budgetSummary.totalContractBudgetPerComplete || 0,
          },
        ];
        const updated = { ...local, budgetRows: initialRows };
        setLocalCoverPage(updated);
        onChange(updated);
      } else {
        // Sync first row's amountRecommendedForFunding — and its %Complete, unless typed — from
        // budget summary when it changes
        const firstRow = coverPageRows[0];
        const budgetRecommended = budgetSummary?.totalAmountReccForFunding ?? 0;
        const budgetPercent =
          firstRow?.isPercentCompleteOverridden || !budgetSummary
            ? firstRow?.percentComplete
            : budgetSummary.totalContractBudgetPerComplete || 0;
        const updatedRows =
          firstRow && (firstRow.amountRecommendedForFunding !== budgetRecommended || firstRow.percentComplete !== budgetPercent)
            ? [
                { ...firstRow, amountRecommendedForFunding: budgetRecommended, percentComplete: budgetPercent ?? null },
                ...coverPageRows.slice(1),
              ]
            : coverPageRows;
        const updated = { ...local, budgetRows: updatedRows };
        setLocalCoverPage(updated);
        if (updatedRows !== coverPageRows) {
          onChange(updated);
        }
      }
    } else if (drawRows.length > 0 && hasEmptyRows) {
      const updated = { ...local, budgetRows: drawRows };
      setLocalCoverPage(updated);
      onChange(updated);
    } else if (budgetSummary && hasEmptyRows) {
      // Initialize from budget summary if no rows exist
      const initialRows: CoverPageBudgetRowDTO[] = [];
      if (budgetSummary?.requestedAmount !== undefined || budgetSummary?.totalAmountReccForFunding !== undefined) {
        initialRows.push({
          budgetName: drawName || 'Main Budget',
          requestedAmount: budgetSummary.requestedAmount || 0,
          amountRecommendedForFunding: budgetSummary.totalAmountReccForFunding ?? 0,
          percentComplete: budgetSummary.totalContractBudgetPerComplete || 0,
        });
      }
      if (initialRows.length > 0) {
        const updated = { ...local, budgetRows: initialRows };
        setLocalCoverPage(updated);
        onChange(updated);
      }
    }
  }, [coverPage, budgetSummary, drawName, draws, contractor, onChange]);

  // Calculate totals
  const totals = useMemo(() => {
    const budgetRows = localCoverPage.budgetRows || [];
    const totalRequested = budgetRows.reduce((sum, row) => sum + (row.requestedAmount || 0), 0);
    const totalRecommended = budgetRows.reduce((sum, row) => sum + (row.amountRecommendedForFunding || 0), 0);
    return { totalRequested, totalRecommended };
  }, [localCoverPage.budgetRows]);

  const columns = useMemo<MRT_ColumnDef<CoverPageBudgetRowDTO>[]>(
    () => [
      {
        header: 'G702 Names',
        accessorKey: 'budgetName',
        muiTableHeadCellProps: {
          align: 'left',
        },
        muiTableBodyCellProps: ({ row }) => ({
          align: 'left',
          sx: {
            border: 'none',
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
          },
        }),
        Cell: ({ cell }: { cell: MRT_Cell<CoverPageBudgetRowDTO> }) => {
          const value = cell.getValue<string>() || '';
          return <span>{value}</span>;
        },
        Footer: () => (
          <Typography variant='button' fontWeight='bold'>
            Totals
          </Typography>
        ),
        muiTableFooterCellProps: {
          align: 'left',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
          }),
        },
        muiEditTextFieldProps: ({ row }) => ({
          fullWidth: true,
          size: 'small' as const,
          variant: 'outlined' as const,
          onBlur: event => handleRowChange(row.index, 'budgetName', event.target.value),
        }),
      },
      {
        header: 'Requested Amount',
        accessorKey: 'requestedAmount',
        muiTableHeadCellProps: {
          align: 'left',
        },
        muiTableBodyCellProps: ({ row }) => ({
          align: 'left',
          sx: {
            border: 'none',
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
          },
        }),
        Cell: ({ cell }: { cell: MRT_Cell<CoverPageBudgetRowDTO> }) => {
          let value = cell.getValue<number>();
          // Normalize value: handle null, undefined, '-', NaN by defaulting to 0
          if (value === null || value === undefined || value === ('-' as any) || isNaN(Number(value))) {
            value = 0;
          }
          return <span>{formatCurrency(value.toString(), false, true)}</span>;
        },
        Footer: () => (
          <Typography variant='button' fontWeight='bold'>
            {formatCurrency(totals.totalRequested.toString(), false, true)}
          </Typography>
        ),
        muiTableFooterCellProps: {
          align: 'left',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
        muiEditTextFieldProps: ({ row }) => ({
          fullWidth: true,
          size: 'small' as const,
          type: 'number',
          variant: 'outlined' as const,
          inputProps: { step: '0.01', min: '0' },
          onBlur: event => handleRowChange(row.index, 'requestedAmount', parseFloat(event.target.value) || 0),
        }),
      },
      {
        header: 'Amount Recommended for Funding',
        accessorKey: 'amountRecommendedForFunding',
        muiTableHeadCellProps: {
          align: 'left',
        },
        muiTableBodyCellProps: ({ row }) => ({
          align: 'left',
          sx: {
            border: 'none',
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
          },
        }),
        Cell: ({ cell }: { cell: MRT_Cell<CoverPageBudgetRowDTO> }) => {
          let value = cell.getValue<number>();
          // Normalize value: handle null, undefined, '-', NaN by defaulting to 0
          if (value === null || value === undefined || value === ('-' as any) || isNaN(Number(value))) {
            value = 0;
          }
          return <span>{formatCurrency(value.toString(), false, true)}</span>;
        },
        Footer: () => (
          <Typography variant='button' fontWeight='bold'>
            {formatCurrency(totals.totalRecommended.toString(), false, true)}
          </Typography>
        ),
        muiTableFooterCellProps: {
          align: 'left',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
        muiEditTextFieldProps: ({ row }) => ({
          fullWidth: true,
          size: 'small' as const,
          type: 'number',
          variant: 'outlined' as const,
          inputProps: { step: '0.01', min: '0' },
          onBlur: event => handleRowChange(row.index, 'amountRecommendedForFunding', parseFloat(event.target.value) || 0),
        }),
      },
      {
        header: '% Complete',
        accessorKey: 'percentComplete',
        muiTableHeadCellProps: {
          align: 'left',
        },
        muiTableBodyCellProps: ({ row }) => ({
          align: 'left',
          sx: {
            border: 'none',
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
          },
        }),
        Cell: ({ cell }: { cell: MRT_Cell<CoverPageBudgetRowDTO> }) => {
          const value = cell.getValue<number | null>();
          if (value === null || value === undefined) return <span></span>;
          return <span>{roundCompletionPercent(value)}%</span>;
        },
        Footer: () => '',
        muiTableFooterCellProps: {
          align: 'left',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            '&:not(:last-of-type)::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '20%',
              height: '60%',
              width: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
            },
          }),
        },
        muiEditTextFieldProps: ({ row }) => ({
          fullWidth: true,
          size: 'small' as const,
          type: 'number',
          variant: 'outlined' as const,
          inputProps: { step: '0.01', min: '0', max: '100' },
          onBlur: event =>
            handleRowChange(row.index, 'percentComplete', event.target.value === '' ? null : parseFloat(event.target.value) || 0),
        }),
      },
    ],
    [handleRowChange, totals],
  );

  const table = useMaterialReactTable<CoverPageBudgetRowDTO>({
    columns,
    data: localCoverPage.budgetRows || [],
    layoutMode: 'grid',
    enableEditing: true,
    editDisplayMode: 'cell',
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableSorting: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableRowActions: true,
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        borderRadius: 0,
        border: 'none',
        backgroundColor: 'transparent',
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: '0px',
        maxHeight: '400px',
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: () => {
      return {
        // Every header is left-aligned and bottom-anchored, regardless of how its body cells align,
        // so multi-line and single-line headers line up on the same baseline.
        align: 'left' as const,
        sx: {
          backgroundColor: theme.palette.primary.main + ' !important',
          color: '#ffffff',
          padding: '10px 12px',
          border: 'none',
          verticalAlign: 'bottom',
          justifyContent: 'flex-start',
          // MRT truncates header labels with an ellipsis by default, which makes narrow columns
          // behave inconsistently (some wrap, some clip). Always wrap onto the next line instead.
          '& .Mui-TableHeadCell-Content': {
            minWidth: 0,
            alignItems: 'flex-end',
          },
          '& .Mui-TableHeadCell-Content-Labels': {
            minWidth: 0,
            alignItems: 'flex-end',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            minWidth: 0,
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            overflowWrap: 'anywhere',
            textAlign: 'left',
          },
          '&:not(:last-of-type)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '20%',
            height: '60%',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
          },
        },
      };
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    }),
    renderRowActions: ({ row }) => {
      // Draw-backed rows are derived from the report's covered draws — change the draw selection
      // to remove one. Only hand-added rows are deletable here.
      const isDrawBackedRow = Boolean((row.original as CoverPageBudgetRowDTO)?.drawId);
      return (
        <Box sx={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
          <Tooltip title={isDrawBackedRow ? 'This row comes from a selected draw' : 'Delete row'} arrow={false}>
            <span>
              <WhiteFab
                size='small'
                sx={{ width: 32, height: 32, minHeight: 32 }}
                icon={<Delete fontSize='small' />}
                textColor='error'
                onClick={() => handleDeleteRow(row)}
                disabled={isDrawBackedRow || !localCoverPage.budgetRows || localCoverPage.budgetRows.length === 1}
              />
            </span>
          </Tooltip>
        </Box>
      );
    },
    positionActionsColumn: 'last',
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ['mrt-row-actions'] },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 50,
        muiTableHeadCellProps: {
          align: 'center',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff',
            justifyContent: 'center',
            padding: '10px 12px',
            border: 'none',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            '&[data-pinned="true"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 10,
            },
          }),
        },
        Header: () => null,
        muiTableBodyCellProps: ({ row }) => ({
          align: 'center',
          sx: {
            border: 'none',
            backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
            '&[data-pinned="true"]::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
              zIndex: -1,
            },
            '&[data-pinned="true"]': {
              zIndex: 5,
            },
          },
        }),
        Footer: () => '',
        muiTableFooterCellProps: {
          align: 'center',
          sx: (theme: any) => ({
            backgroundColor: theme.palette.primary.main + ' !important',
            color: '#ffffff !important',
            fontSize: '0.9rem',
            padding: '16px 12px',
            border: 'none',
            '&[data-pinned="true"]::before': {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
            },
            '&[data-pinned="true"]': {
              zIndex: 1,
            },
          }),
        },
      },
    },
  });

  return (
    <>
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddRowDialog} fullWidth maxWidth='sm'>
        <DialogTitle>Add Row</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Name'
              type='text'
              fullWidth
              value={newBudgetRowForm.budgetName}
              onChange={event =>
                setNewBudgetRowForm(prev => ({
                  ...prev,
                  budgetName: event.target.value,
                }))
              }
            />
            <TextField
              label='Requested Amount'
              type='number'
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              value={newBudgetRowForm.requestedAmount}
              onChange={event =>
                setNewBudgetRowForm(prev => ({
                  ...prev,
                  requestedAmount: event.target.value,
                }))
              }
            />
            <TextField
              label='Amount Recommended for Funding'
              type='number'
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              value={newBudgetRowForm.amountRecommendedForFunding}
              onChange={event =>
                setNewBudgetRowForm(prev => ({
                  ...prev,
                  amountRecommendedForFunding: event.target.value,
                }))
              }
            />
            <TextField
              label='% Complete'
              type='number'
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              value={newBudgetRowForm.percentComplete}
              onChange={event =>
                setNewBudgetRowForm(prev => ({
                  ...prev,
                  percentComplete: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddRowDialog} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddRow}
            variant='contained'
            disabled={isAddRowDisabled}
            sx={{
              color: isAddRowDisabled ? undefined : theme.palette.common.white,
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ mt: 3, mb: 3 }}>
        <Accordion
          expanded={expanded}
          onChange={() => onExpandedChange?.(!expanded)}
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px !important',
            px: 2,
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
            <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' pr={2}>
              <Box display='flex' alignItems='center' gap={1}>
                {onToggleComplete && (
                  <Box onClick={e => e.stopPropagation()}>
                    <SectionCompleteToggle isCompleted={isCompleted} onToggle={onToggleComplete} isSaving={isSavingComplete} />
                  </Box>
                )}
                <Typography variant='h5' color='primary' sx={{ mb: 0, opacity: isCompleted ? 0.6 : 1 }}>
                  Amounts to include in the Cover Letter and Executive Summary
                </Typography>
              </Box>
              <Box display='flex' alignItems='center' gap={1} onClick={e => e.stopPropagation()}>
                <IconOverTextButton
                  icon={<RefreshIcon />}
                  text='Refresh'
                  tooltip='Refresh each budget row with the latest G702/3 values'
                  onClick={handleRefreshFromBudget}
                  showHoverEffect={true}
                  disabled={!budgetSummary || isCompleted}
                />
                <IconOverTextButton
                  icon={<AddIcon />}
                  text='Add Row'
                  tooltip='Add new row'
                  onClick={openAddRowDialog}
                  showHoverEffect={true}
                  disabled={isCompleted}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, pb: 2, position: 'relative' }}>
            {isCompleted && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, cursor: 'not-allowed' }} onClickCapture={e => e.stopPropagation()} />
            )}
            <Box sx={isCompleted ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
              <Box sx={{ overflowX: 'auto', px: 2 }}>
                <Box sx={{ minWidth: '100%' }}>
                  <MaterialReactTable table={table} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', px: 2, pb: 2, pt: 2, alignItems: 'stretch', minHeight: 160 }}>
                {/* Controls (graph preview now lives in the Schedule module) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                  <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 0 }}>
                    Magazine Cover Items:
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant='body2' color='text.primary' sx={{ minWidth: '220px' }}>
                      Graph Type:
                    </Typography>
                    <FormControl sx={{ minWidth: 300 }}>
                      <InputLabel>Graph Type</InputLabel>
                      <Select
                        value={localCoverPage.graphType || 'standard'}
                        label='Graph Type'
                        onChange={e => handleGraphTypeChange(e.target.value as CoverPageGraphType)}
                        sx={{
                          height: '40px',
                          '& .MuiSelect-select': {
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <MenuItem value='standard'>Standard (% Complete + Schedule)</MenuItem>
                        <MenuItem value='reallocations'>% Complete + Reallocations</MenuItem>
                        <MenuItem value='none'>No Graphs</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant='body2' color='text.primary' sx={{ minWidth: '220px' }}>
                      Contingency Percentage Used:
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                        width: '120px',
                        height: '40px',
                        textAlign: 'center',
                        backgroundColor: isContingencyNA ? 'action.hover' : 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isContingencyNA ? (
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                          N/A
                        </Typography>
                      ) : (
                        <TextField
                          type='number'
                          value={contingencyInputValue}
                          onChange={e => setContingencyInputValue(e.target.value)}
                          onBlur={e => handleContingencyChange(e.target.value)}
                          variant='standard'
                          inputProps={{ step: '0.01', min: '0', max: '100' }}
                          placeholder=''
                          sx={{
                            width: '100%',
                            '& .MuiInput-underline:before': { display: 'none' },
                            '& .MuiInput-underline:after': { display: 'none' },
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              padding: 0,
                              fontSize: '0.875rem',
                            },
                            '& input::-webkit-outer-spin-button': { display: 'none' },
                            '& input::-webkit-inner-spin-button': { display: 'none' },
                            '& input[type=number]': { MozAppearance: 'textfield' },
                          }}
                          InputProps={{
                            endAdornment: contingencyInputValue !== '' ? '%' : null,
                          }}
                        />
                      )}
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size='small'
                          checked={isContingencyNA}
                          onChange={e => handleContingencyNAChange(e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={
                        <Typography variant='body2' color='text.secondary'>
                          N/A
                        </Typography>
                      }
                      sx={{ ml: 0, mr: 0, gap: 0.5 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant='body2' color='text.primary' sx={{ minWidth: '220px' }}>
                      Is the Balance sufficient?
                    </Typography>
                    <Box
                      role='button'
                      tabIndex={0}
                      aria-pressed={!!localCoverPage.isBalanceSufficient}
                      onClick={() => handleBalanceSufficientChange(!localCoverPage.isBalanceSufficient)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBalanceSufficientChange(!localCoverPage.isBalanceSufficient);
                        }
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                        width: '120px',
                        height: '40px',
                        textAlign: 'center',
                        backgroundColor: 'background.paper',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        },
                      }}
                    >
                      <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.875rem' }}>
                        {localCoverPage.isBalanceSufficient ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
};

export default CoverPageTable;
